import React, { FC, useContext, useState } from 'react';
import CircleCheckFill from '@gravity-ui/icons/CircleCheckFill';
import CircleExclamation from '@gravity-ui/icons/CircleExclamation';
import Clock from '@gravity-ui/icons/Clock';
import ShieldCheck from '@gravity-ui/icons/ShieldCheck';
import Xmark from '@gravity-ui/icons/Xmark';
import { Button, CardTheme, Icon, Label, LabelProps, Modal, TextProps } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { OperationType } from 'client/model/operation';
import { DraftStatus, DraftStatusSmartHome } from 'client/model/draft';
import * as keyset from 'i18n/DraftsStatuses';

import styles from './OperationStatus.module.scss';

const t = i18n(keyset);

export const STATUS_TO_THEME_MAP: Record<
    OperationType | DraftStatus,
    { color: TextProps['color']; theme: CardTheme; icon: React.JSX.Element | undefined; label: string } | undefined
> = {
    skillCreated: undefined,
    skillWithdrawn: {
        label: t('installable-app-status-withdraw'),
        color: 'warning-heavy',
        theme: 'warning',
        icon: <Clock />,
    },
    skillStopped: {
        label: t('installable-app-status-stopped'),
        color: 'danger-heavy',
        theme: 'danger',
        icon: <CircleExclamation />,
    },
    skillDeleted: {
        label: t('installable-app-status-deleted'),
        color: 'danger-heavy',
        theme: 'danger',
        icon: <CircleExclamation />,
    },
    reviewRequested: {
        label: t('installable-app-status-review-request'),
        color: 'warning-heavy',
        theme: 'warning',
        icon: <Clock />,
    },
    reviewCancelled: {
        label: t('installable-app-status-review-canceled'),
        color: 'danger-heavy',
        theme: 'danger',
        icon: <CircleExclamation />,
    },
    reviewApproved: {
        label: t('installable-app-status-review-approved'),
        color: 'info-heavy',
        theme: 'info',
        icon: <ShieldCheck />,
    },
    deployRequested: {
        label: t('installable-app-status-deploy-request'),
        color: 'warning-heavy',
        theme: 'warning',
        icon: <Clock />,
    },
    deployCompleted: {
        label: t('installable-app-status-published'),
        color: 'positive-heavy',
        theme: 'success',
        icon: <CircleCheckFill />,
    },
    deployRejected: {
        label: t('installable-app-status-deploy-rejected'),
        color: 'danger-heavy',
        theme: 'danger',
        icon: <CircleExclamation />,
    },
    settingsReviewApproved: {
        label: t('installable-app-status-settings-review-approved'),
        color: 'info-heavy',
        theme: 'info',
        icon: <ShieldCheck />,
    },
    deviceTestPrepared: {
        label: t('installable-app-status-device-test-prepared'),
        color: 'info-heavy',
        theme: 'info',
        icon: <Clock />,
    },
    deviceTestStarted: {
        label: t('installable-app-status-device-test-started'),
        color: 'info-heavy',
        theme: 'info',
        icon: <Clock />,
    },
    deviceTestCompleted: {
        label: t('installable-app-status-device-test-completed'),
        color: 'info-heavy',
        theme: 'success',
        icon: <CircleCheckFill />,
    },

    inDevelopment: undefined,
    skillNominated: undefined,
    skillNominationRotationCompleted: undefined,
    skillNominationRotationWithdrawn: undefined,
};

const MAP_THEME_TO_ICON_STYLE: Record<Extract<LabelProps['theme'], string>, string> = {
    clear: styles.operationStatusIcon_clear,
    normal: styles.operationStatusIcon_clear,
    utility: styles.operationStatusIcon_clear,
    unknown: styles.operationStatusIcon_clear,
    danger: styles.operationStatusIcon_danger,
    info: styles.operationStatusIcon_info,
    success: styles.operationStatusIcon_success,
    warning: styles.operationStatusIcon_warning,
};

const DRAFT_STATUSES_OVERRIDE = [OperationType.ReviewCancelled, OperationType.DeployRejected];
const OPERATIONS_DISPLAYED_FOR_PUBLISHED_SKILL = [OperationType.SkillWithdrawn, OperationType.SkillStopped];

export const getDisplayDraftStatus = (
    isDraft: boolean,
    draftStatus: DraftStatus | DraftStatusSmartHome,
    lastOperationType: OperationType,
) => {
    if (lastOperationType === OperationType.SkillDeleted) return lastOperationType;
    if (isDraft) {
        if (draftStatus === DraftStatus.InDevelopment && DRAFT_STATUSES_OVERRIDE.includes(lastOperationType)) {
            return lastOperationType;
        }
        return draftStatus as DraftStatus;
    } else {
        return OPERATIONS_DISPLAYED_FOR_PUBLISHED_SKILL.includes(lastOperationType)
            ? lastOperationType
            : OperationType.DeployCompleted;
    }
};

export const OperationStatus: FC<{ isDraft: boolean }> = ({ isDraft }) => {
    const [openModal, setOpenModal] = useState(false);
    const {
        aliceApp,
        lastOperationType = OperationType.SkillCreated,
        lastOperationComment,
    } = useContext(CurrentAliceAppContext)!;

    const displayOperationType: OperationType | DraftStatus = getDisplayDraftStatus(
        isDraft,
        aliceApp.draft.status,
        lastOperationType,
    );

    const displayLastOperationComment = DRAFT_STATUSES_OVERRIDE.includes(lastOperationType) ? lastOperationComment : '';
    if (!STATUS_TO_THEME_MAP[displayOperationType] || !STATUS_TO_THEME_MAP[displayOperationType]?.['label'])
        return null;

    const handleLabelClick =
        displayOperationType !== OperationType.DeployCompleted && displayLastOperationComment
            ? () => setOpenModal(true)
            : undefined;

    const operationIcon = STATUS_TO_THEME_MAP[displayOperationType]?.['icon'];
    return (
        <div className={styles.operationStatusContainer}>
            <span className={styles.operationDesktopStyling}>{' • '}</span>
            <Label
                className={styles.operationStatusLabel}
                theme={STATUS_TO_THEME_MAP[displayOperationType]?.['theme'] || 'clear'}
                icon={STATUS_TO_THEME_MAP[displayOperationType]?.['icon']}
                onClick={handleLabelClick}
                size="m"
            >
                <span className={styles.operationDesktopStyling}>
                    {STATUS_TO_THEME_MAP[displayOperationType]?.['label']}
                </span>
            </Label>
            <div
                className={clx(
                    styles.operationStatusIcon,
                    MAP_THEME_TO_ICON_STYLE[STATUS_TO_THEME_MAP[displayOperationType]?.['theme'] || 'unknown'],
                )}
                onClick={handleLabelClick}
            >
                {operationIcon}
            </div>
            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <div className={styles.operationModal__container}>
                    <header className={styles.operationModal__header}>
                        <div className={styles.operationModal__headerContent}>
                            {STATUS_TO_THEME_MAP[displayOperationType]?.icon}
                            {STATUS_TO_THEME_MAP[displayOperationType]?.label}
                        </div>
                        <Button
                            className={styles.operationModal__closeButton}
                            onClick={() => setOpenModal(false)}
                            view="flat"
                            size="l"
                        >
                            <Icon data={Xmark} />
                        </Button>
                    </header>
                    <div className={styles.operationModal__content}>{lastOperationComment}</div>
                    <footer className={styles.operationModal__footer}>
                        <Button onClick={() => setOpenModal(false)} view="action">
                            {t('it-is-ok')}
                        </Button>
                    </footer>
                </div>
            </Modal>
        </div>
    );
};
